import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';
import { DragUpdate, DropResult } from 'react-beautiful-dnd';
import {
  IWorkspaceSliceEntity,
  IWorkspaceSliceRow,
  IWorkspaceSlice,
} from './types';
import { IWorkspaceRowFieldsFragment } from '../../../utils/apollo/graphqlSchema';

export const MAX_ANALYSES_PER_ROW = 2;

const initialDraggingProps = {
  draggableId: '',
  highlightedElement: '',
  lastElement: false,
  chartsLengthExceeded: false,
};

const initialState: IWorkspaceSlice = {
  id: '',
  name: '',
  description: '',
  createdAt: '',
  creator: null,
  rows: [],
  // snapshot is used for edit mode only. It will store a slice copy
  snapshot: null,
  selectedElement: null,
  draggingProps: initialDraggingProps,
  isEditing: false,
  isDragging: false,
};

// Parse rows for drag&drop
const parseWorkspaceRows = (
  rows: IWorkspaceRowFieldsFragment[],
): IWorkspaceSliceRow[] =>
  rows.map((row) => ({
    ...row,
    analysesIds: row.analyses.map((analysis) => analysis.id),
  }));

export default createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    populateWorkspace: (
      state,
      action: PayloadAction<Partial<IWorkspaceSliceEntity>>,
    ) => {
      return {
        ...state,
        ...action.payload,
        rows: action.payload?.rows
          ? parseWorkspaceRows(action.payload.rows)
          : state.rows,
      };
    },
    clearWorkspace: () => {
      return initialState;
    },
    renameWorkspace: (
      state,
      action: PayloadAction<Pick<IWorkspaceSlice, 'name' | 'description'>>,
    ) => {
      const { name, description } = action.payload;

      state.name = name;
      state.description = description;

      return;
    },
    workspaceEditActivate: (state) => {
      // Make a copy of current state as a snapshot
      state.snapshot = { ...state };
      state.isEditing = true;

      const firstAnalysis = state.rows[0]?.analyses?.[0]?.id;

      if (firstAnalysis) {
        state.selectedElement = firstAnalysis;
      }

      return;
    },
    workspaceEditSave: (state) => {
      state.isEditing = false;
      state.snapshot = null;
      state.selectedElement = null;

      return;
    },
    workspaceEditCancel: (state) => {
      // Restoring workspace to before-edit state (snapshot)
      return { ...state.snapshot, isEditing: false, selectedElement: null };
    },
    workspaceDragSetSelected: (state, action: PayloadAction<string>) => {
      state.selectedElement = action.payload;

      return;
    },
    workspaceDragStart: (state, action: PayloadAction<string>) => {
      const draggableId = action.payload;

      // Getting a snapshot of the current state
      const currentState = current(state);
      const currentStateRows = currentState.rows;

      state.isDragging = true;

      /*
        Fill rows with temporary elements to be able to
        place draggable element above, below, or next to another element
      */
      state.rows = [
        {
          id: `temporary-00`,
          analyses: [],
          analysesIds: [],
          __typename: 'WorkspaceRow',
        },
        ...currentStateRows.reduce<IWorkspaceSliceRow[]>((acc, row, index) => {
          const remainingAnalysesOnCurrentRow = row.analysesIds.filter(
            (analysisId) => analysisId !== draggableId,
          );

          if (remainingAnalysesOnCurrentRow.length > 0) {
            return [
              ...acc,
              row,
              {
                id: `temporary-${index}`,
                analyses: [],
                analysesIds: [],
                __typename: 'WorkspaceRow',
              },
            ];
          }

          return [...acc, row];
        }, []),
      ];

      return;
    },
    workspaceDragUpdate: (state, action: PayloadAction<DragUpdate>) => {
      const update = action.payload;

      const draggableId = update.draggableId;

      const destinationRowId = update?.destination?.droppableId;
      const destinationRowIndex = state.rows.findIndex(
        (row) => row.id === destinationRowId,
      );
      const draggableDestinationIndex = update?.destination?.index ?? 0;

      if (!destinationRowId || destinationRowIndex === -1) {
        state.draggingProps = initialDraggingProps;
        return;
      }

      /* Find the indicator place */
      const destinationRowsFilteredAnalyses = state.rows[
        destinationRowIndex
      ].analysesIds.filter((analysisId) => analysisId !== draggableId);
      if (destinationRowsFilteredAnalyses.length > 0) {
        let lastElement = false;
        let chartsLengthExceeded = false;
        let highlightedElement =
          destinationRowsFilteredAnalyses[draggableDestinationIndex];
        if (!highlightedElement) {
          lastElement = true;
          highlightedElement =
            destinationRowsFilteredAnalyses[draggableDestinationIndex - 1];
        }

        if (destinationRowsFilteredAnalyses.length === MAX_ANALYSES_PER_ROW) {
          chartsLengthExceeded = true;
        }

        state.draggingProps = {
          ...state.draggingProps,
          highlightedElement,
          lastElement,
          draggableId,
          chartsLengthExceeded,
        };
        return;
      }

      state.draggingProps = initialDraggingProps;
      return;
    },
    workspaceDragEnd: (state, action: PayloadAction<DropResult>) => {
      const result = action.payload;
      const { draggableId, source, destination } = result;

      const currentState = current(state);
      const currentStateRows = currentState.rows;

      const clearTempRows = () => {
        state.rows = [...state.rows].filter(
          (row) => !row.id.includes('temporary') && row.analysesIds.length > 0,
        );
      };

      state.isDragging = false;
      state.draggingProps = initialDraggingProps;

      if (
        !destination ||
        (destination.droppableId === source.droppableId &&
          destination.index === source.index)
      ) {
        clearTempRows();
        return;
      }

      const sourceRowId = source.droppableId;
      const sourceIndex = source.index;
      const sourceRowIndex = currentStateRows.findIndex(
        (row) => row.id === sourceRowId,
      );

      const destinationRowId = destination.droppableId;
      const destinationIndex = destination.index;
      const destinationRowIndex = currentStateRows.findIndex(
        (row) => row.id === destinationRowId,
      );

      if (destinationRowIndex === -1 || sourceRowIndex === -1) {
        clearTempRows();
        return;
      }

      const destinationRowFilteredAnalyses = currentStateRows[
        destinationRowIndex
      ].analysesIds.filter((analysisId) => analysisId !== draggableId);

      // Limit of analysis per row
      if (destinationRowFilteredAnalyses.length === MAX_ANALYSES_PER_ROW) {
        clearTempRows();
        return;
      }

      state.rows[sourceRowIndex].analysesIds.splice(sourceIndex, 1);

      if (currentStateRows[destinationRowIndex].id.includes('temporary')) {
        let latestRowId = currentStateRows
          .filter((row) => !row.id.includes('temporary'))
          .reduce((acc: number, val) => Math.max(acc, parseInt(val.id, 10)), 0);
        latestRowId += 1;

        state.rows[destinationRowIndex].id = latestRowId.toString();
      }

      state.rows[destinationRowIndex].analysesIds.splice(
        destinationIndex,
        0,
        draggableId,
      );

      clearTempRows();
      return;
    },
  },
});
